import { useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import {
  useSettings,
  useUpdateSettings,
} from '@sparklayer-io/spark-dash-core/context/settings-context';
import { SettingToggleRow } from '@sparklayer-io/spark-dash-pages/components/setting-toggle-row';
import { StaffBadge } from '@sparklayer-io/spark-dash-pages/components/staff-badge';
import { StaffOnly } from '@sparklayer-io/spark-dash-pages/components/staff-only';
import { Card, Divider } from '@ui/themed';
import { t } from 'i18next';
import type { I18nPrefix } from '../../../i18n/prefix';
import { PLATFORM_DOCS_URLS } from '../constants';
import { NestedToggleRow } from './nested-toggle-row';

const ORDER_ACTIONS_I18N =
  'features:platform.pages.platform_page.order_actions' satisfies I18nPrefix;

export function ShopifyOrderActionsCard() {
  const env = useEnv();
  const settings = useSettings();
  const { mutate } = useUpdateSettings();

  const salesAgentsAllowed =
    env.billing.sales_agents_allowed === -1 || !!env.billing.sales_agents_allowed;

  return (
    <Card>
      <Card.Header headingLevel="h2">{t(`${ORDER_ACTIONS_I18N}.title`)}</Card.Header>
      <Card.Body>
        <SettingToggleRow
          label={t(`${ORDER_ACTIONS_I18N}.auto_complete.label`)}
          description={t(`${ORDER_ACTIONS_I18N}.auto_complete.description`)}
          checked={settings.draftOrdersAutoComplete}
          onChange={(checked) => mutate({ key: 'draftOrdersAutoComplete', value: checked })}
          learnMoreUrl={PLATFORM_DOCS_URLS.autoComplete}
        />

        {settings.draftOrdersAutoComplete && (
          <div className="divide-y divide-gray-200 overflow-hidden rounded-md border border-gray-200">
            <NestedToggleRow
              label={t(`${ORDER_ACTIONS_I18N}.account_set_as_paid`)}
              checked={settings.draftOrdersAutoCompleteAccountSetAsPaid}
              onChange={(checked) =>
                mutate({ key: 'draftOrdersAutoCompleteAccountSetAsPaid', value: checked })
              }
            />
            <NestedToggleRow
              label={t(`${ORDER_ACTIONS_I18N}.invoice_set_as_paid`)}
              checked={settings.draftOrdersAutoCompleteInvoiceSetAsPaid}
              onChange={(checked) =>
                mutate({ key: 'draftOrdersAutoCompleteInvoiceSetAsPaid', value: checked })
              }
            />
          </div>
        )}

        {salesAgentsAllowed && (
          <>
            <Divider />
            <SettingToggleRow
              label={t(`${ORDER_ACTIONS_I18N}.disable_invite.label`)}
              description={t(`${ORDER_ACTIONS_I18N}.disable_invite.description`)}
              checked={settings.disableNewCustomerInvitation}
              onChange={(checked) =>
                mutate({ key: 'disableNewCustomerInvitation', value: checked })
              }
              learnMoreUrl={PLATFORM_DOCS_URLS.disableInvite}
            />
          </>
        )}

        <StaffOnly>
          <Divider />
          <SettingToggleRow
            label={
              <>
                {t(`${ORDER_ACTIONS_I18N}.create_draft.label`)}
                <StaffBadge />
              </>
            }
            description={t(`${ORDER_ACTIONS_I18N}.create_draft.description`)}
            checked={settings.createDraftOrderOnCalculate}
            onChange={(checked) => mutate({ key: 'createDraftOrderOnCalculate', value: checked })}
          />
        </StaffOnly>

        <StaffOnly>
          <Divider />
          <SettingToggleRow
            label={
              <>
                {t(`${ORDER_ACTIONS_I18N}.price_override.label`)}
                <StaffBadge />
              </>
            }
            description={t(`${ORDER_ACTIONS_I18N}.price_override.description`)}
            checked={settings.draftOrdersPriceOverrideEnabled}
            onChange={(checked) =>
              mutate({ key: 'draftOrdersPriceOverrideEnabled', value: checked })
            }
          />
        </StaffOnly>
      </Card.Body>
    </Card>
  );
}
